import { useLocalParticipant, useTrackToggle } from "@livekit/components-react"
import { useMediaQuery, useTheme } from "@mui/material"
import { Track, TrackPublication } from "livekit-client"
import React, { useEffect, useRef, useState } from "react"
import { useAppContext } from "../../app/Context"

interface UserVideoLiveKitProps {
  track?: TrackPublication
}

export function UserVideoLiveKit({ track }: UserVideoLiveKitProps) {
  const [isVisible, setIsVisible] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const context = useAppContext()
  const { localParticipant } = useLocalParticipant()

  const { enabled: isEnabled } = useTrackToggle({
    source: Track.Source.Camera,
    initialState: context.videoEnabled,
  })

  useEffect(() => {
    const handleResize = () => {
      setIsVisible(false)
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    }

    window.addEventListener("resize", handleResize)
    handleResize()

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const attachTrack = async () => {
      if (!track?.track || !videoRef.current) return

      if (!isEnabled) {
        videoRef.current.srcObject = null
        return
      }

      try {
        const mediaStream = new MediaStream()
        mediaStream.addTrack(track.track.mediaStreamTrack)
        videoRef.current.srcObject = mediaStream
        await videoRef.current.play()
      } catch (error) {
        console.error("Error attaching track:", error)
      }
    }

    attachTrack()

    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [track, isEnabled])

  useEffect(() => {
    if (!videoRef.current || !track?.source || !context.videoDevice) return

    if (
      track.source === Track.Source.Camera &&
      context.stream instanceof MediaStream &&
      context.stream.getVideoTracks().length > 0
    ) {
      videoRef.current.srcObject = context.stream
    }
  }, [context.stream, context.videoDevice])

  const isVideoAvailable = localParticipant && track && isEnabled

  const mobileDimension = "clamp(96px, 26vw, 140px)"
  const videoWidth = isMobile ? mobileDimension : "200px"
  const videoHeight = isMobile ? mobileDimension : "144px"

  return (
    <div
      className="user-video-container"
      style={{
        position: "absolute",
        display: isVisible ? "block" : "none",
        width: videoWidth,
        height: videoHeight,
        left: "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)",
        bottom: isMobile
          ? "var(--spacing-tokens-size-in-px-spacing-spacing-9xl)"
          : "var(--spacing-tokens-size-in-px-spacing-spacing-2xl)",
        zIndex: 5,
        borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-lg)",
        overflow: "hidden",
        boxShadow: "var(--call-pip-shadow)",
      }}
    >
      {isVideoAvailable ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover object-top scale-x-[-1]"
        />
      ) : (
        <div
          className="w-full h-full"
          style={{ backgroundColor: "var(--call-chip-bg-active)" }}
        />
      )}
    </div>
  )
}
